/**
 * I18n API Service
 *
 * Cliente API para o modulo tmf_i18n (Python/FastAPI).
 * Gerencia traducoes, idiomas e mensagens do sistema.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03
 */

import axios, { AxiosInstance } from 'axios';
import { getJwtToken } from './apiClient';
import type {
  Language,
  LanguageListResponse,
  LanguageCreateRequest,
  LanguageUpdateRequest,
  TranslationResponse,
  TranslationSetRequest,
  TranslationUpdateResponse,
  TranslationBatchRequest,
  TranslationBatchResponse,
  TranslatableModelsResponse,
  MessagesResponse,
  CacheStats,
} from '@/types/i18n';

// API base URL para o modulo i18n (Python backend)
// Usa URL relativa para que Nginx/Ingress faça proxy para o backend
const I18N_API_BASE = '/api/v1/i18n';

/**
 * Cria cliente axios configurado para API i18n
 */
const createI18nClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: I18N_API_BASE,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 3000, // Timeout curto - fallback para idiomas estáticos
  });

  // Request interceptor - adicionar token se disponivel (com validação)
  client.interceptors.request.use(
    (config) => {
      const token = getJwtToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - tratamento de erros
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Log de erro apenas em modo debug (não poluir console)
      // Erros de rede são esperados quando a API i18n não está rodando
      // O sistema usa fallback para idiomas estáticos
      if (import.meta.env.DEV && error.response) {
        console.debug('[i18n] API Error:', {
          status: error.response.status,
          url: error.config?.url,
        });
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Instancia singleton
let i18nClientInstance: AxiosInstance | null = null;

const getI18nClient = (): AxiosInstance => {
  if (!i18nClientInstance) {
    i18nClientInstance = createI18nClient();
  }
  return i18nClientInstance;
};

// ===== Language API =====

/**
 * Lista idiomas. Por padrão retorna apenas ativos (soft-delete).
 * Passar `activeOnly=false` para incluir desativados.
 */
export const getLanguages = async (activeOnly: boolean = true): Promise<LanguageListResponse> => {
  interface ApiResponse {
    items: Language[];
    total: number;
    default_language?: string;
  }
  const response = await getI18nClient().get<ApiResponse>('/languages', {
    params: { active_only: activeOnly },
  });
  // Mapeia resposta da API para formato esperado pelo frontend
  return {
    languages: response.data.items || [],
    total: response.data.total || 0,
    active_count: (response.data.items || []).filter(l => l.status === 'active').length,
  };
};

/**
 * Lista apenas idiomas ativos
 */
export const getActiveLanguages = async (): Promise<Language[]> => {
  const response = await getI18nClient().get<{ languages: Language[] }>('/languages/active');
  return response.data.languages;
};

/**
 * Obtem idioma padrao
 */
export const getDefaultLanguage = async (): Promise<Language> => {
  const response = await getI18nClient().get<Language>('/languages/default');
  return response.data;
};

/**
 * Obtem idioma por ID
 */
export const getLanguageById = async (id: number): Promise<Language> => {
  const response = await getI18nClient().get<Language>(`/languages/${id}`);
  return response.data;
};

/**
 * Obtem idioma por codigo
 */
export const getLanguageByCode = async (code: string): Promise<Language> => {
  const response = await getI18nClient().get<Language>(`/languages/code/${code}`);
  return response.data;
};

/**
 * Cria novo idioma
 */
export const createLanguage = async (data: LanguageCreateRequest): Promise<Language> => {
  const response = await getI18nClient().post<Language>('/languages', data);
  return response.data;
};

/**
 * Atualiza idioma existente
 */
export const updateLanguage = async (id: number, data: LanguageUpdateRequest): Promise<Language> => {
  const response = await getI18nClient().put<Language>(`/languages/${id}`, data);
  return response.data;
};

/**
 * Exclui idioma (soft delete - desativa)
 */
export const deleteLanguage = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await getI18nClient().delete<{ success: boolean; message: string }>(`/languages/${id}`);
  return response.data;
};

/**
 * Alterna status do idioma (ativo/inativo)
 */
export const toggleLanguageStatus = async (id: number): Promise<Language> => {
  const response = await getI18nClient().patch<Language>(`/languages/${id}/toggle`);
  return response.data;
};

/**
 * Define idioma como padrao
 */
export const setDefaultLanguage = async (id: number): Promise<Language> => {
  const response = await getI18nClient().patch<Language>(`/languages/${id}/default`);
  return response.data;
};

// ===== Translation API =====

/**
 * Obtem traducao para um modelo/registro
 */
export const getTranslation = async (
  model: string,
  modelId: number,
  languageCode?: string,
  field?: string
): Promise<TranslationResponse> => {
  const params: Record<string, string> = {};
  if (languageCode) params.language_code = languageCode;
  if (field) params.field = field;

  const response = await getI18nClient().get<TranslationResponse>(
    `/translate/${model}/${modelId}`,
    { params }
  );
  return response.data;
};

/**
 * Obtem traducao de campo especifico
 */
export const getTranslationField = async (
  model: string,
  modelId: number,
  field: string,
  languageCode?: string
): Promise<TranslationResponse> => {
  const params: Record<string, string> = {};
  if (languageCode) params.language_code = languageCode;

  const response = await getI18nClient().get<TranslationResponse>(
    `/translate/${model}/${modelId}/${field}`,
    { params }
  );
  return response.data;
};

/**
 * Obtem traducoes em batch
 */
export const getTranslationsBatch = async (
  request: TranslationBatchRequest
): Promise<TranslationBatchResponse> => {
  const response = await getI18nClient().post<TranslationBatchResponse>(
    '/translate/batch',
    request
  );
  return response.data;
};

/**
 * Define/atualiza traducao
 */
export const setTranslation = async (
  model: string,
  modelId: number,
  request: TranslationSetRequest
): Promise<TranslationUpdateResponse> => {
  const response = await getI18nClient().put<TranslationUpdateResponse>(
    `/translate/${model}/${modelId}`,
    request
  );
  return response.data;
};

/**
 * Lista modelos traduziveis
 */
export const getTranslatableModels = async (): Promise<TranslatableModelsResponse> => {
  const response = await getI18nClient().get<TranslatableModelsResponse>('/translate/models');
  return response.data;
};

// ===== Message API (System Messages) =====

/**
 * Obtem mensagens por categoria
 */
export const getMessages = async (
  category: string,
  languageCode?: string
): Promise<MessagesResponse> => {
  const params: Record<string, string> = {};
  if (languageCode) params.language_code = languageCode;

  const response = await getI18nClient().get<MessagesResponse>(
    `/messages/${category}`,
    { params }
  );
  return response.data;
};

/**
 * Obtem mensagem especifica
 */
export const getMessage = async (
  category: string,
  key: string,
  languageCode?: string
): Promise<string> => {
  const params: Record<string, string> = {};
  if (languageCode) params.language_code = languageCode;

  const response = await getI18nClient().get<{ translation: string }>(
    `/messages/${category}/${key}`,
    { params }
  );
  return response.data.translation;
};

// ===== Cache API =====

/**
 * Obtem estatisticas do cache
 */
export const getCacheStats = async (): Promise<CacheStats> => {
  const response = await getI18nClient().get<CacheStats>('/cache/stats');
  return response.data;
};

/**
 * Invalida cache
 */
export const invalidateCache = async (
  model?: string,
  modelId?: number,
  languageCode?: string
): Promise<{ invalidated: number }> => {
  const response = await getI18nClient().post<{ invalidated: number }>(
    '/cache/invalidate',
    { model, model_id: modelId, language_code: languageCode }
  );
  return response.data;
};

// ===== Health API =====

/**
 * Health check
 */
export const healthCheck = async (): Promise<{ status: string; module: string }> => {
  const response = await getI18nClient().get<{ status: string; module: string }>('/health');
  return response.data;
};

/**
 * Readiness check
 */
export const readinessCheck = async (): Promise<{ status: string; checks: Record<string, boolean> }> => {
  const response = await getI18nClient().get<{ status: string; checks: Record<string, boolean> }>('/health/ready');
  return response.data;
};

// ===== Tenant Overrides API =====

/**
 * Obtém overrides de tradução do tenant.
 * Retorna {} silenciosamente se o tenant não tiver overrides ou em caso de erro de rede.
 * O tenant é derivado pelo backend (settings.TENANT_ID do pod) — sem IDOR.
 *
 * @returns Record de overrides aninhado {namespace: {key: value}}
 */
export const getTenantOverrides = async (): Promise<Record<string, Record<string, string>>> => {
  try {
    const response = await getI18nClient().get<Record<string, Record<string, string>>>(
      '/overrides'
    );
    return response.data ?? {};
  } catch {
    // Graceful: nunca quebrar o boot do app por overrides ausentes
    if (import.meta.env.DEV) {
      console.debug('[i18n] Nao ha overrides para este tenant');
    }
    return {};
  }
};

// ===== Export API Object =====

export const i18nApi = {
  // Languages
  getLanguages,
  getActiveLanguages,
  getDefaultLanguage,
  getLanguageById,
  getLanguageByCode,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  toggleLanguageStatus,
  setDefaultLanguage,

  // Translations
  getTranslation,
  getTranslationField,
  getTranslationsBatch,
  setTranslation,
  getTranslatableModels,

  // Messages
  getMessages,
  getMessage,

  // Tenant Overrides
  getTenantOverrides,

  // Cache
  getCacheStats,
  invalidateCache,

  // Health
  healthCheck,
  readinessCheck,
};

export default i18nApi;
